import { useState, MouseEvent } from 'react';
import {
  Avatar,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Stack,
  Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';

import { useUser } from '@/features/users/api/get-user';

export interface UserSectionProps {
  userId: string | undefined;
  profilePath: string;
  logoutPath: string;
}

export const UserSection = ({ userId, profilePath, logoutPath }: UserSectionProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const { data: user } = useUser(userId);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    navigate(logoutPath, { viewTransition: true });
    handleClose();
  };

  if (!user) {
    return (
      <Box display='flex' alignItems='center' gap={1} p={2} maxWidth='100%'>
        <Skeleton variant='circular' width={40} height={40} />
        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
          <Skeleton variant='text' width='60%' height={24} />
          <Skeleton variant='text' width='80%' height={20} />
        </Stack>
        <Skeleton variant='circular' width={24} height={24} />
      </Box>
    );
  }

  return (
    <>
      <Box display='flex' alignItems='center' gap={1} p={2} maxWidth='100%'>
        <Avatar sx={{ width: 40, height: 40 }}>
          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
        </Avatar>
        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant='subtitle2' textOverflow='ellipsis' noWrap>
            {user.name}
          </Typography>
          <Typography variant='caption' color='text.secondary' textOverflow='ellipsis' noWrap>
            {user.email || ''}
          </Typography>
        </Stack>
        <IconButton
          aria-label='more'
          id='user-menu-button'
          aria-controls={open ? 'user-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      <Menu
        id='user-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'user-menu-button',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={Link} to={profilePath} onClick={handleClose} viewTransition>
          <ListItemIcon>
            <PersonIcon fontSize='small' />
          </ListItemIcon>
          {t('navigation.menu.profile')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          {t('navigation.menu.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};
